import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface Behavior {
  id: string;
  name: string;
  category: string;
  frequency?: number;
  timing?: string;
  quantity?: number;
  unit?: string;
}

export interface JournalEntry {
  id: string;
  date: string;
  behaviors: {
    [key: string]: {
      value: boolean;
      frequency?: number;
      timing?: string;
      quantity?: number;
    };
  };
  metrics?: {
    recovery?: number;
    strain?: number;
    sleep?: {
      quality: number;
      duration: number;
    };
  };
  notes: string;
}

interface JournalState {
  selectedBehaviors: Behavior[];
  entries: JournalEntry[];
  addBehavior: (behavior: Behavior) => void;
  removeBehavior: (id: string) => void;
  addEntry: (entry: JournalEntry) => void;
  updateEntry: (id: string, entry: Partial<JournalEntry>) => void;
  getEntryById: (id: string) => JournalEntry | undefined;
  getEntriesByDateRange: (startDate: string, endDate: string) => JournalEntry[];
  getBehaviorsByCategory: (category: string) => Behavior[];
  getImpactAnalysis: (behaviorId: string) => {
    recovery?: number;
    strain?: number;
    sleep?: number;
  } | null;
}

const initialState = {
  selectedBehaviors: [],
  entries: []
};

export const useJournalStore = create<JournalState>()(
  persist(
    (set, get) => ({
      ...initialState,
      
      addBehavior: (behavior) =>
        set((state) => ({
          selectedBehaviors: [...state.selectedBehaviors, behavior],
        })),
        
      removeBehavior: (id) =>
        set((state) => ({
          selectedBehaviors: state.selectedBehaviors.filter((b) => b.id !== id),
        })),
        
      addEntry: (entry) =>
        set((state) => ({
          entries: [...state.entries, entry],
        })),
        
      updateEntry: (id, updatedEntry) =>
        set((state) => ({
          entries: state.entries.map((entry) =>
            entry.id === id ? { ...entry, ...updatedEntry } : entry
          ),
        })),
        
      getEntryById: (id) => {
        const state = get();
        return state.entries.find((entry) => entry.id === id);
      },
      
      getEntriesByDateRange: (startDate, endDate) => {
        const state = get();
        return state.entries.filter(
          (entry) => entry.date >= startDate && entry.date <= endDate
        );
      },
      
      getBehaviorsByCategory: (category) => {
        const state = get();
        return state.selectedBehaviors.filter((b) => b.category === category);
      },
      
      getImpactAnalysis: (behaviorId) => {
        const state = get();
        const entriesWithBehavior = state.entries.filter(
          (entry) => entry.behaviors[behaviorId]?.value
        );
        const entriesWithoutBehavior = state.entries.filter(
          (entry) => !entry.behaviors[behaviorId]?.value
        );

        if (entriesWithBehavior.length === 0 || entriesWithoutBehavior.length === 0) {
          return null;
        }

        const withMetrics = entriesWithBehavior.reduce(
          (acc, entry) => ({
            recovery: acc.recovery + (entry.metrics?.recovery || 0),
            strain: acc.strain + (entry.metrics?.strain || 0),
            sleep: acc.sleep + (entry.metrics?.sleep?.quality || 0),
            count: acc.count + 1
          }),
          { recovery: 0, strain: 0, sleep: 0, count: 0 }
        );

        const withoutMetrics = entriesWithoutBehavior.reduce(
          (acc, entry) => ({
            recovery: acc.recovery + (entry.metrics?.recovery || 0),
            strain: acc.strain + (entry.metrics?.strain || 0),
            sleep: acc.sleep + (entry.metrics?.sleep?.quality || 0),
            count: acc.count + 1
          }),
          { recovery: 0, strain: 0, sleep: 0, count: 0 }
        );

        return {
          recovery: withMetrics.count > 0 && withoutMetrics.count > 0
            ? (withMetrics.recovery / withMetrics.count) - (withoutMetrics.recovery / withoutMetrics.count)
            : undefined,
          strain: withMetrics.count > 0 && withoutMetrics.count > 0
            ? (withMetrics.strain / withMetrics.count) - (withoutMetrics.strain / withoutMetrics.count)
            : undefined,
          sleep: withMetrics.count > 0 && withoutMetrics.count > 0
            ? (withMetrics.sleep / withMetrics.count) - (withoutMetrics.sleep / withoutMetrics.count)
            : undefined
        };
      },
    }),
    {
      name: 'journal-storage',
      version: 1,
      migrate: (persistedState: any, version: number) => {
        if (version === 0) {
          return {
            ...initialState,
            ...persistedState,
          };
        }
        return persistedState as JournalState;
      },
    }
  )
);